/**
 * Utility functions for ExtraHorizon SDK operations
 */

import { ExhApiError } from "./types";

// The SDK serializes the response body into `error.message` as a JSON string.
const parseErrorBody = (message: string): Record<string, unknown> | null => {
  try {
    const parsed = JSON.parse(message);
    return typeof parsed === "object" && parsed !== null ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
};

/**
 * Parse SDK errors into a consistent format.
 *
 * The ExtraHorizon SDK throws `ApiError` instances whose response body lives on
 * `error.response`, the HTTP status on `error.status`, and a human-readable
 * description on `error.message`. We fall back to parsing `error.message` as
 * JSON for legacy callers/tests that still serialize the body that way.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const parseExhError = (error: any): ExhApiError => {
  if (error && typeof error === "object") {
    const responseBody =
      error.response && typeof error.response === "object" && !Array.isArray(error.response)
        ? (error.response as Record<string, unknown>)
        : null;
    const messageBody = typeof error.message === "string" ? parseErrorBody(error.message) : null;
    const body = responseBody ?? messageBody ?? undefined;

    return {
      message: error.message || "An unknown error occurred",
      code: (body?.code as string | undefined) || error.code || error.qName || error.name || "UNKNOWN_ERROR",
      statusCode: error.status ?? error.statusCode,
      body,
    };
  }

  return {
    message: String(error),
    code: "UNKNOWN_ERROR",
    statusCode: undefined,
  };
};

/**
 * Extract a `{ field: message }` validation errors map from a thrown SDK error,
 * if the response body contains one. Returns null otherwise.
 *
 * Supports two ExtraHorizon error shapes:
 *  - `{ errors: { fieldPath: "message", ... } }` — used by service-level
 *    handlers that emit per-field messages.
 *  - `{ badFormatFields: ["fieldPath", ...], message: "..." }` — emitted by
 *    the schema/document service for `FIELD_FORMAT_EXCEPTION` (HTTP 400)
 *    when a value violates the schema's format constraints.
 */
export const extractValidationErrors = (error: unknown): Record<string, string> | null => {
  const parsed = parseExhError(error);
  const result: Record<string, string> = {};

  const errors = parsed.body?.errors;
  if (errors && typeof errors === "object") {
    for (const [key, value] of Object.entries(errors)) {
      if (typeof value === "string") result[key] = value;
    }
  }

  const badFormatFields = parsed.body?.badFormatFields;
  if (Array.isArray(badFormatFields)) {
    const message = (parsed.body?.message as string | undefined) ?? parsed.message;
    for (const field of badFormatFields) {
      if (typeof field === "string" && !(field in result)) {
        result[field] = message;
      }
    }
  }

  return Object.keys(result).length > 0 ? result : null;
};

/**
 * Check if SDK is available in the window context
 */
export const isExhSDKAvailable = (): boolean => {
  return typeof window !== "undefined";
};
